import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Paper,
  Stack,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  AutoAwesome as AutoAwesomeIcon,
  RateReview as RateReviewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import { AdminService } from '../../services/adminService';
import TaxonomyReassignModal from '../../components/admin/TaxonomyReassignModal';
import TaxonomyGenerateModal from '../../components/admin/TaxonomyGenerateModal';
import { navigateToFirstExercise } from '../../utils/exerciseListNavigation';
import { getAssociationFitAssessment } from '../../config/shared_config';

const VALIDATION_COLUMNS = [
  { key: 'nouveau', label: 'Nouveau', color: 'default' },
  { key: 'en_attente', label: 'En attente', color: 'warning' },
  { key: 'valide', label: 'Validé', color: 'success' },
];

const ORPHAN_REASON_LABELS = {
  orphan_domain: 'Domaine inconnu',
  orphan_competence: 'Compétence invalide',
  orphan_notion: 'Notion absente du référentiel',
  missing_notion: 'Notion non renseignée',
};

function emptyCounts() {
  return { nouveau: 0, en_attente: 0, valide: 0, total: 0 };
}

function resolveCounts(node, gameTypeFilter) {
  if (!node) return emptyCounts();
  if (!gameTypeFilter) {
    return {
      nouveau: node.nouveau ?? 0,
      en_attente: node.en_attente ?? 0,
      valide: node.valide ?? 0,
      total: node.total ?? 0,
    };
  }
  return node.byGameType?.[gameTypeFilter] || emptyCounts();
}

function buildLabelLookup(validTriplets = []) {
  const domains = {};
  const competences = {};
  const notions = {};
  validTriplets.forEach((triplet) => {
    domains[triplet.domain] = triplet.domainLabel;
    competences[`${triplet.domain}|${triplet.competence}`] = triplet.competenceLabel;
    notions[`${triplet.domain}|${triplet.competence}|${triplet.notion}`] = triplet.notionLabel;
  });
  return { domains, competences, notions };
}

function CountCell({ value, muted = false }) {
  if (!value) {
    return (
      <Typography variant="body2" color="text.disabled" align="center">
        —
      </Typography>
    );
  }
  return (
    <Typography
      variant="body2"
      align="center"
      sx={{ fontWeight: muted ? 400 : 600, color: muted ? 'text.secondary' : 'text.primary' }}
    >
      {value}
    </Typography>
  );
}

function GameTypeBreakdown({ node, gameTypes, taxonomyTriplet, onNavigate }) {
  if (!node?.byGameType || !gameTypes?.length) return null;

  const entries = gameTypes
    .map((gt) => ({ ...gt, counts: node.byGameType[gt.id] }))
    .filter((entry) => entry.counts?.total > 0);

  if (entries.length === 0) return null;

  const associationAssessment = taxonomyTriplet?.domain && taxonomyTriplet?.competence
    ? getAssociationFitAssessment(
      taxonomyTriplet.domain,
      taxonomyTriplet.competence,
      taxonomyTriplet.notion,
    )
    : null;

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.75 }}>
      {entries.map(({ id, label, counts }) => {
        const associationMismatch = id === 'association' && associationAssessment && !associationAssessment.fit;
        const tooltip = associationMismatch
          ? `${associationAssessment.reason} — Ouvrir la liste`
          : `Nouveau: ${counts.nouveau} · En attente: ${counts.en_attente} · Validé: ${counts.valide} — Ouvrir le 1er exercice`;
        return (
          <Tooltip key={id} title={tooltip}>
            <Chip
              size="small"
              variant="outlined"
              color={associationMismatch ? 'warning' : 'default'}
              icon={associationMismatch ? <WarningIcon fontSize="small" /> : undefined}
              label={`${label}: ${counts.total}`}
              clickable
              onClick={() => onNavigate?.({ ...taxonomyTriplet, gameType: id })}
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

function ValidationCountsRow({
  node,
  notionLabel,
  notionKey,
  muted = false,
  gameTypeFilter,
  gameTypes,
  taxonomyTriplet,
  onNavigate,
  onGenerate,
}) {
  const counts = resolveCounts(node, gameTypeFilter);
  const isMuted = muted || counts.total === 0;
  const associationAssessment = taxonomyTriplet?.domain && taxonomyTriplet?.competence
    ? getAssociationFitAssessment(
      taxonomyTriplet.domain,
      taxonomyTriplet.competence,
      taxonomyTriplet.notion,
    )
    : null;
  const showAssociationWarning = associationAssessment && !associationAssessment.fit
    && (
      gameTypeFilter === 'association'
      || (node?.byGameType?.association?.total > 0)
    );

  return (
    <TableRow sx={{ backgroundColor: isMuted ? 'grey.50' : 'white' }}>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
          {notionKey}
        </Typography>
        {notionLabel !== notionKey && (
          <Typography variant="caption" color="text.secondary">
            {notionLabel}
          </Typography>
        )}
        {showAssociationWarning && (
          <Tooltip title={associationAssessment.reason}>
            <Typography variant="caption" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <WarningIcon sx={{ fontSize: 14 }} />
              Association peu adaptée à cette notion
            </Typography>
          </Tooltip>
        )}
        {!gameTypeFilter && (
          <GameTypeBreakdown
            node={node}
            gameTypes={gameTypes}
            taxonomyTriplet={taxonomyTriplet}
            onNavigate={onNavigate}
          />
        )}
      </TableCell>
      {VALIDATION_COLUMNS.map((col) => (
        <TableCell key={col.key} align="center">
          <CountCell value={counts[col.key]} muted={isMuted} />
        </TableCell>
      ))}
      <TableCell align="center">
        {counts.total > 0 && onNavigate ? (
          <Tooltip title={gameTypeFilter ? 'Ouvrir le 1er exercice filtré' : 'Tous les types — ouvrir le 1er exercice'}>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              clickable
              label={gameTypeFilter ? String(counts.total) : `Tous: ${counts.total}`}
              onClick={() => onNavigate({
                ...taxonomyTriplet,
                ...(gameTypeFilter ? { gameType: gameTypeFilter } : {}),
              })}
            />
          </Tooltip>
        ) : (
          <CountCell value={counts.total} muted={isMuted} />
        )}
      </TableCell>
      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <Tooltip title="Générer un ou plusieurs exercices LLM">
            <span>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => onGenerate?.({
                  taxonomyTriplet,
                  notionLabel,
                  gameType: gameTypeFilter || undefined,
                })}
              >
                Générer
              </Button>
            </span>
          </Tooltip>
          {counts.total > 0 && onNavigate && (
            <Tooltip title="Relire le premier exercice de cette notion">
              <Button
                size="small"
                variant="outlined"
                startIcon={<RateReviewIcon />}
                onClick={() => onNavigate({
                  ...taxonomyTriplet,
                  ...(gameTypeFilter ? { gameType: gameTypeFilter } : {}),
                })}
              >
                Relire
              </Button>
            </Tooltip>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function CompetenceSection({
  domain,
  competence,
  notions,
  labelLookup,
  gameTypeFilter,
  gameTypes,
  onNavigate,
  onGenerate,
}) {
  const competenceLabel = labelLookup.competences[`${domain}|${competence}`] || competence;
  const notionEntries = Object.entries(notions || {})
    .filter(([, node]) => !gameTypeFilter || resolveCounts(node, gameTypeFilter).total > 0)
    .sort((a, b) => {
      const diff = resolveCounts(b[1], gameTypeFilter).total - resolveCounts(a[1], gameTypeFilter).total;
      if (diff !== 0) return diff;
      return a[0].localeCompare(b[0], 'fr');
    });
  const competenceTotal = notionEntries.reduce(
    (sum, [, node]) => sum + resolveCounts(node, gameTypeFilter).total,
    0
  );

  if (gameTypeFilter && competenceTotal === 0) return null;

  return (
    <Accordion disableGutters variant="outlined" sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
          <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
            {competence}
          </Typography>
          {competenceLabel !== competence && (
            <Typography variant="body2" color="text.secondary">
              ({competenceLabel})
            </Typography>
          )}
          <Chip
            size="small"
            label={`${competenceTotal} exercice${competenceTotal > 1 ? 's' : ''}`}
            color={competenceTotal > 0 ? 'primary' : 'default'}
            variant={competenceTotal > 0 ? 'filled' : 'outlined'}
            sx={{ ml: 'auto' }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Notion</TableCell>
                {VALIDATION_COLUMNS.map((col) => (
                  <TableCell key={col.key} align="center" sx={{ fontWeight: 'bold' }}>
                    {col.label}
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Workflow</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notionEntries.map(([notion, node]) => (
                <ValidationCountsRow
                  key={notion}
                  node={node}
                  notionKey={notion}
                  notionLabel={labelLookup.notions[`${domain}|${competence}|${notion}`] || notion}
                  muted={resolveCounts(node, gameTypeFilter).total === 0}
                  gameTypeFilter={gameTypeFilter}
                  gameTypes={gameTypes}
                  taxonomyTriplet={{ domain, competence, notion }}
                  onNavigate={onNavigate}
                  onGenerate={onGenerate}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
}

function DomainSection({
  domain,
  competences,
  labelLookup,
  gameTypeFilter,
  gameTypes,
  onNavigate,
  onGenerate,
}) {
  const domainLabel = labelLookup.domains[domain] || domain;
  const competenceEntries = Object.entries(competences || {});
  const domainTotal = competenceEntries.reduce((sum, [, notions]) => {
    return sum + Object.values(notions).reduce(
      (s, node) => s + resolveCounts(node, gameTypeFilter).total,
      0
    );
  }, 0);

  if (gameTypeFilter && domainTotal === 0) return null;

  return (
    <Accordion defaultExpanded={domainTotal > 0} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
          <Typography variant="h6" sx={{ fontFamily: 'monospace', fontSize: '1rem' }}>
            {domain}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {domainLabel}
          </Typography>
          <Chip
            size="small"
            label={`${domainTotal} exercice${domainTotal > 1 ? 's' : ''}`}
            color={domainTotal > 0 ? 'primary' : 'default'}
            sx={{ ml: 'auto' }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {competenceEntries
          .sort((a, b) => a[0].localeCompare(b[0], 'fr'))
          .map(([competence, notions]) => (
            <CompetenceSection
              key={competence}
              domain={domain}
              competence={competence}
              notions={notions}
              labelLookup={labelLookup}
              gameTypeFilter={gameTypeFilter}
              gameTypes={gameTypes}
              onNavigate={onNavigate}
              onGenerate={onGenerate}
            />
          ))}
      </AccordionDetails>
    </Accordion>
  );
}

function KpiCard({ title, value, subtitle, icon, color = 'primary' }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          {icon}
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
        </Stack>
        <Typography variant="h4" color={`${color}.main`}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function ExerciseTaxonomyScreen() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [reassignSource, setReassignSource] = useState(null);
  const [reassignCount, setReassignCount] = useState(0);
  const [successMessage, setSuccessMessage] = useState(null);
  const [gameTypeFilter, setGameTypeFilter] = useState('');
  const [generateTarget, setGenerateTarget] = useState(null);
  const navigate = useNavigate();

  const goToExerciseList = useCallback((params) => {
    navigateToFirstExercise(navigate, params);
  }, [navigate]);

  const openGenerateModal = useCallback((target) => {
    setGenerateTarget(target);
  }, []);

  const handleGenerateSuccess = useCallback(async ({ createdIds, warnings }) => {
    const count = createdIds?.length ?? 0;
    let message = `${count} exercice${count > 1 ? 's' : ''} généré${count > 1 ? 's' : ''} — ouverture en relecture`;
    if (warnings?.length) {
      message += ` (avertissement : ${warnings[0]})`;
    }
    setSuccessMessage(message);
    if (createdIds?.[0]) {
      navigate(`/admin/exercises/edit/${createdIds[0]}`);
    }
    try {
      const stats = await AdminService.getExerciseTaxonomyStats();
      setData(stats);
    } catch (err) {
      console.error('Erreur rafraîchissement stats:', err);
    }
  }, [navigate]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const stats = await AdminService.getExerciseTaxonomyStats();
      setData(stats);
    } catch (err) {
      console.error('Erreur chargement stats taxonomie:', err);
      setError('Impossible de charger les statistiques de taxonomie des exercices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const summary = data?.summary;
  const tree = data?.tree || {};
  const orphans = data?.orphans || [];
  const missingTaxonomy = data?.missingTaxonomy;
  const validTriplets = data?.validTriplets || [];
  const gameTypes = data?.gameTypes || [];
  const labelLookup = useMemo(() => buildLabelLookup(validTriplets), [validTriplets]);

  const filteredSummary = useMemo(() => {
    if (!summary) return null;
    if (!gameTypeFilter) return summary;
    const selected = gameTypes.find((gt) => gt.id === gameTypeFilter);
    const total = selected?.counts?.total ?? 0;
    return { ...summary, totalExercises: total };
  }, [summary, gameTypeFilter, gameTypes]);

  const visibleOrphans = useMemo(() => {
    if (!gameTypeFilter) return orphans;
    return orphans.filter(
      (row) => resolveCounts(row.counts, gameTypeFilter).total > 0
    );
  }, [orphans, gameTypeFilter]);

  const missingTaxonomyCounts = useMemo(
    () => resolveCounts(missingTaxonomy, gameTypeFilter),
    [missingTaxonomy, gameTypeFilter]
  );

  const openReassignModal = (triplet, count) => {
    setReassignSource(triplet);
    setReassignCount(count);
    setSuccessMessage(null);
  };

  const handleReassignSuccess = (result) => {
    if (result?.stats) {
      setData(result.stats);
    }
    setSuccessMessage(
      `${result?.updatedCount ?? 0} exercice${(result?.updatedCount ?? 0) > 1 ? 's' : ''} réassigné${(result?.updatedCount ?? 0) > 1 ? 's' : ''} vers ${result?.target?.domain}/${result?.target?.competence}/${result?.target?.notion}`
    );
  };

  return (
    <AdminLayout>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Couverture taxonomique des exercices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Référentiel servi par le backend (copie de{' '}
            <code>frontend/src/utils/shared_config.js</code> synchronisée au déploiement).
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchStats}
          disabled={loading}
        >
          Actualiser
        </Button>
      </Box>

      {data && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Type d&apos;exercice</InputLabel>
            <Select
              value={gameTypeFilter}
              label="Type d'exercice"
              onChange={(e) => setGameTypeFilter(e.target.value)}
            >
              <MenuItem value="">
                <em>Tous les types ({summary?.totalExercises ?? 0})</em>
              </MenuItem>
              {gameTypes.map((gt) => (
                <MenuItem key={gt.id} value={gt.id}>
                  {gt.label} ({gt.counts?.total ?? 0})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && (
        <Alert
          severity={successMessage.includes('avertissement') ? 'warning' : 'success'}
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : data && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <KpiCard
                title="Exercices total"
                value={filteredSummary?.totalExercises ?? 0}
                subtitle={gameTypeFilter
                  ? 'Filtré par type de jeu'
                  : `${summary?.withValidTaxonomy ?? 0} avec taxonomie valide`}
                icon={<CheckCircleIcon color="primary" fontSize="small" />}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <KpiCard
                title="Triplets couverts"
                value={`${summary?.coveredTriplets ?? 0} / ${summary?.referentialTriplets ?? 0}`}
                subtitle="Notions du référentiel ayant au moins 1 exercice"
                icon={<CheckCircleIcon color="success" fontSize="small" />}
                color="success"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <KpiCard
                title="Triplets orphelins"
                value={gameTypeFilter
                  ? visibleOrphans.reduce((s, o) => s + resolveCounts(o.counts, gameTypeFilter).total, 0)
                  : (summary?.orphanExercises ?? 0)}
                subtitle={`${visibleOrphans.length} combinaison${visibleOrphans.length > 1 ? 's' : ''} distincte${visibleOrphans.length > 1 ? 's' : ''}`}
                icon={<WarningIcon color="warning" fontSize="small" />}
                color="warning"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <KpiCard
                title="Sans taxonomie"
                value={missingTaxonomyCounts.total}
                subtitle="Domaine ou compétence manquant"
                icon={<ErrorIcon color="error" fontSize="small" />}
                color="error"
              />
            </Box>
          </Stack>

          <Card sx={{ mb: 3 }}>
            <CardHeader
              title="Référentiel shared_config"
              subheader={gameTypeFilter
                ? 'Comptages filtrés par type de jeu — détail par type visible sans filtre'
                : 'Tous les triplets valides, avec répartition par type sous chaque notion'}
            />
            <CardContent>
              {Object.keys(tree).length === 0 ? (
                <Alert severity="warning">Référentiel vide — vérifiez shared_config.</Alert>
              ) : (
                Object.keys(tree)
                  .sort((a, b) => a.localeCompare(b, 'fr'))
                  .map((domain) => (
                    <DomainSection
                      key={domain}
                      domain={domain}
                      competences={tree[domain]}
                      labelLookup={labelLookup}
                      gameTypeFilter={gameTypeFilter}
                      gameTypes={gameTypes}
                      onNavigate={goToExerciseList}
                      onGenerate={openGenerateModal}
                    />
                  ))
                  .filter(Boolean)
              )}
            </CardContent>
          </Card>

          {(visibleOrphans.length > 0 || missingTaxonomyCounts.total > 0) && (
            <Card>
              <CardHeader
                title="Triplets non reliés au référentiel"
                subheader="Présents dans les exercices mais absents ou invalides dans shared_config"
                avatar={<WarningIcon color="warning" />}
              />
              <CardContent>
                {missingTaxonomyCounts.total > 0 && (
                  <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={(
                      <Button
                        color="inherit"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => openReassignModal(
                          { domain: '', competence: '', notion: '' },
                          missingTaxonomyCounts.total
                        )}
                      >
                        Réassigner
                      </Button>
                    )}
                  >
                    <strong>{missingTaxonomyCounts.total}</strong> exercice{missingTaxonomyCounts.total > 1 ? 's' : ''} sans
                    domaine ou compétence renseigné
                    {' '}(nouveau: {missingTaxonomyCounts.nouveau}, en attente: {missingTaxonomyCounts.en_attente}, validé: {missingTaxonomyCounts.valide})
                    {!gameTypeFilter && (
                      <Box sx={{ mt: 1 }}>
                        <GameTypeBreakdown
                          node={missingTaxonomy}
                          gameTypes={gameTypes}
                          taxonomyTriplet={{ domain: '', competence: '', notion: '' }}
                          onNavigate={goToExerciseList}
                        />
                      </Box>
                    )}
                  </Alert>
                )}

                {visibleOrphans.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.100' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Domaine</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Compétence</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Notion</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Problème</TableCell>
                          {VALIDATION_COLUMNS.map((col) => (
                            <TableCell key={col.key} align="center" sx={{ fontWeight: 'bold' }}>
                              {col.label}
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleOrphans.map((row) => {
                          const counts = resolveCounts(row.counts, gameTypeFilter);
                          return (
                          <TableRow key={`${row.domain}|${row.competence}|${row.notion}`} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                {row.domain || '—'}
                              </Typography>
                              {row.domain && labelLookup.domains[row.domain] && (
                                <Typography variant="caption" color="text.secondary">
                                  {labelLookup.domains[row.domain]}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                {row.competence || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                {row.notion || '—'}
                              </Typography>
                              {!gameTypeFilter && (
                                <GameTypeBreakdown
                                  node={row.counts}
                                  gameTypes={gameTypes}
                                  taxonomyTriplet={{
                                    domain: row.domain,
                                    competence: row.competence,
                                    notion: row.notion,
                                  }}
                                  onNavigate={goToExerciseList}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Tooltip title={row.reason}>
                                <Chip
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  label={ORPHAN_REASON_LABELS[row.reason] || row.reason}
                                />
                              </Tooltip>
                            </TableCell>
                            {VALIDATION_COLUMNS.map((col) => (
                              <TableCell key={col.key} align="center">
                                <CountCell value={counts[col.key]} />
                              </TableCell>
                            ))}
                            <TableCell align="center">
                              {counts.total > 0 ? (
                                <Tooltip title="Tous les types — ouvrir le 1er exercice">
                                  <Chip
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    clickable
                                    label={`Tous: ${counts.total}`}
                                    onClick={() => goToExerciseList({
                                      domain: row.domain,
                                      competence: row.competence,
                                      notion: row.notion,
                                    })}
                                  />
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" fontWeight="bold">0</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => openReassignModal(
                                  {
                                    domain: row.domain,
                                    competence: row.competence,
                                    notion: row.notion,
                                  },
                                  counts.total
                                )}
                              >
                                Réassigner
                              </Button>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="success">Aucun triplet orphelin détecté.</Alert>
                )}
              </CardContent>
            </Card>
          )}

          {visibleOrphans.length === 0 && missingTaxonomyCounts.total === 0 && !gameTypeFilter && (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              Tous les exercices sont correctement rattachés au référentiel shared_config.
            </Alert>
          )}
        </>
      )}

      <TaxonomyGenerateModal
        open={Boolean(generateTarget)}
        onClose={() => setGenerateTarget(null)}
        taxonomyTriplet={generateTarget?.taxonomyTriplet}
        notionLabel={generateTarget?.notionLabel}
        defaultGameType={generateTarget?.gameType || gameTypeFilter}
        onSuccess={handleGenerateSuccess}
      />

      <TaxonomyReassignModal
        open={Boolean(reassignSource)}
        onClose={() => setReassignSource(null)}
        sourceTriplet={reassignSource}
        exerciseCount={reassignCount}
        validTriplets={validTriplets}
        onSuccess={handleReassignSuccess}
      />
    </AdminLayout>
  );
}
